import { BusinessMerchantServiceChargeFee } from "../../../../../interfaces/businessFull.interface";
import {
  IBucketGroups,
  IOnlinePurchPriceProfile,
  IPurchPriceProfile,
  OnlinePartnerBucketGroups,
} from "../../../../../interfaces/partner.interface";
import { inPersonGroupKeyForFee } from "./pricingNewTab.helpers";

export type Band = { min?: number; max?: number; valueType?: string };

export const unitFor = (valueType?: string): string =>
  valueType === "Percentage" ? "%" : valueType === "Fixed" ? "€" : "";

// One definition of the band hint, shared by the online bucket rows and the
// in-person fee rows so the two sections can't drift into different wording.
export const formatBand = (band: Band): string => {
  const u = unitFor(band.valueType);
  const suffix = u ? ` ${u}` : "";
  const hasMin = typeof band.min === "number";
  const hasMax = typeof band.max === "number";
  if (hasMin && hasMax)
    return `Partner band: ${band.min} – ${band.max}${suffix}`;
  if (hasMin) return `Partner band: ≥ ${band.min}${suffix}`;
  return `Partner band: ≤ ${band.max}${suffix}`;
};

export const isOutOfBand = (value: unknown, band: Band): boolean => {
  if (typeof value !== "number" || !(value > 0)) return false;
  if (typeof band.min === "number" && value < band.min) return true;
  if (typeof band.max === "number" && value > band.max) return true;
  return false;
};

// Whether a band refuses a rate outright: a unit it cannot be compared in, or a
// value outside its bounds. Absolute, and the online pane applies it that way at
// both levels — a rate already stored out of band is marked and blocks the save
// rather than being grandfathered through on the strength of being old.
export const bandRefuses = (
  rate: { value: number | null; valueType: string },
  band: Band
): boolean => {
  const bounded = typeof band.min === "number" || typeof band.max === "number";
  if (bounded && !!band.valueType && rate.valueType !== band.valueType) {
    return true;
  }
  return isOutOfBand(rate.value, band);
};

// How far outside its band a rate sits, 0 when inside. Mirrors the backend's own
// differential rule — a rate not pushed further out than it already is stays
// acceptable there. The pane no longer reads it: PC-936 made the per-row check
// absolute, so this describes the write path alone.
export const bandDistance = (value: unknown, band: Band): number => {
  if (typeof value !== "number" || !(value > 0)) return 0;
  if (typeof band.min === "number" && value < band.min) return band.min - value;
  if (typeof band.max === "number" && value > band.max) return value - band.max;
  return 0;
};

export const NOT_COMPARABLE_HINT =
  "Partner band is in a different unit — not comparable";

// A bounded band in another unit is refused by the write path, so the row says
// what to do rather than that the two cannot be compared.
export const UNIT_MISMATCH_HINT = "Unit must match the partner band";

// The preview handed the pane no band for this row. It says nothing about why:
// the same status covers a partner with no online range and one priced on the
// legacy four buckets, which is a range, just not in these groups.
export const NO_PARTNER_RANGE_HINT = "No partner range to check";

// A merchant fee and the partner bucket that governs it are only comparable
// when both are expressed in the same unit. A Percentage fee against a Fixed
// euro bucket has no meaningful floor, cap or difference: 1.39 % is neither
// above nor below €1.12, and 1.39 − 1.12 = 0.27 is not a number in any unit.
export const isComparable = (
  feeValueType?: string,
  bucketValueType?: string
): boolean =>
  !!feeValueType && !!bucketValueType && feeValueType === bucketValueType;

// The partner-profile bucket governing an in-person fee. Coverage from the
// read decides which group a fee name maps to; missing coverage does not guess.
export const inPersonBucketFor = (
  feeName: string,
  profile: IPurchPriceProfile | undefined
) => {
  if (!profile) return undefined;
  const groupKey = inPersonGroupKeyForFee(
    feeName,
    profile.inPersonBucketGroupCoverage
  );
  if (!groupKey) return undefined;
  const bucket = profile.bucketGroups?.[groupKey as keyof IBucketGroups];
  return bucket && bucket.value !== undefined ? bucket : undefined;
};

// `value` is the floor and `max` the cap on a purchase-price profile bucket —
// the pair the Partner Mapper pane renders as "Partner Profile Range".
export const inPersonBandFor = (
  feeName: string,
  profile: IPurchPriceProfile | undefined
): Band | undefined => {
  const bucket = inPersonBucketFor(feeName, profile);
  return bucket
    ? { min: bucket.value, max: bucket.max, valueType: bucket.valueType }
    : undefined;
};

// The band governing an online bucket. The online price range the merchant is
// mapped to wins for every bucket it prices — a range stores its floor in
// `value`, where the partner default stores that same floor in `min` — and the
// partner default covers the rest.
export const onlineBandFor = (
  key: keyof OnlinePartnerBucketGroups,
  source: {
    profile?: IOnlinePurchPriceProfile;
    partnerDefault?: OnlinePartnerBucketGroups;
  }
): Band | undefined => {
  const rangeBucket = source.profile?.bucketGroups?.[key];
  const rangeMax =
    typeof rangeBucket?.max === "number"
      ? rangeBucket.max
      : rangeBucket?.value === 0
        ? 0
        : undefined;
  if (typeof rangeBucket?.value === "number" && typeof rangeMax === "number") {
    return {
      min: rangeBucket.value,
      max: rangeMax,
      valueType: rangeBucket.valueType,
    };
  }
  const bucket = source.partnerDefault?.[key];
  const zeroBand: Band = {
    min: 0,
    max: 0,
    valueType: key === "iDeal" || key === "bancontact" ? "Fixed" : "Percentage",
  };
  if (!bucket) {
    return source.profile || source.partnerDefault ? zeroBand : undefined;
  }
  const min =
    typeof bucket.min === "number"
      ? bucket.min
      : bucket.value === 0
        ? 0
        : undefined;
  const max =
    typeof bucket.max === "number"
      ? bucket.max
      : bucket.value === 0
        ? 0
        : undefined;
  return typeof min === "number" && typeof max === "number"
    ? { min, max, valueType: bucket.valueType }
    : undefined;
};

// The band governing a 16-bucket group: `min` is the partner's floor and `max`
// its ceiling, from the pricing preview alone. A group the preview omits has no
// band — the merchant's stored `min`/`max` is a snapshot, not a live range.
export const sixteenBucketOnlineBandFor = (
  key: string,
  source: {
    previewPricing?: Partial<
      Record<
        string,
        {
          min?: number;
          max?: number;
          valueType?: string;
          bandValueType?: string;
        }
      >
    >;
    hasPartner: boolean;
  }
): Band | undefined => {
  if (!source.hasPartner) return undefined;
  const bucket = source.previewPricing?.[key];
  return bucket &&
    (typeof bucket.min === "number" || typeof bucket.max === "number")
    ? {
        min: bucket.min,
        max: bucket.max,
        // One preview record carries both sides: `valueType` is the merchant's
        // own rate, so only `bandValueType` describes the band.
        valueType: bucket.bandValueType ?? bucket.valueType,
      }
    : undefined;
};

export type InPersonComparisonRow = {
  name: string;
  currentValue: string;
  isInRange: boolean;
  comparable: boolean;
  klearlyMarkup: string | null;
  partnerMarkup: string | null;
};

export const formatValue = (value: number, type?: string): string => {
  // Preserve precision (iDeal can be e.g. 0.085); strip float noise and any
  // trailing zeros instead of forcing 2 decimals (which rounded 0.085 → 0.09).
  const n = parseFloat(value.toFixed(4));
  return `${n}${unitFor(type)}`;
};

// Rows for the "Merchant Current In Person Pricing" comparison table: what the
// merchant is charged, Klearly's floor (K) and the partner's cut (P).
//
// A row whose units don't match reports `comparable: false` and no K/P. It also
// reports `isInRange: true`: an incomparable pair is not evidence of a pricing
// violation, so it must not paint the row red or block the save — the mismatch
// is surfaced on its own terms instead.
export const buildInPersonComparison = (
  fees: BusinessMerchantServiceChargeFee[] | undefined,
  profile: IPurchPriceProfile | undefined
): InPersonComparisonRow[] => {
  const rows: InPersonComparisonRow[] = [];
  fees?.forEach((fee) => {
    const bucket = inPersonBucketFor(fee.name, profile);
    if (!bucket) return;
    const currentValue = formatValue(fee.value, fee.valueType);
    if (!isComparable(fee.valueType, bucket.valueType)) {
      rows.push({
        name: fee.name,
        currentValue,
        isInRange: true,
        comparable: false,
        klearlyMarkup: null,
        partnerMarkup: null,
      });
      return;
    }
    const isInRange = fee.value >= bucket.value && fee.value <= bucket.max;
    rows.push({
      name: fee.name,
      currentValue,
      isInRange,
      comparable: true,
      klearlyMarkup: isInRange
        ? formatValue(bucket.value, bucket.valueType)
        : null,
      partnerMarkup: isInRange
        ? formatValue(fee.value - bucket.value, bucket.valueType)
        : null,
    });
  });
  return rows;
};
